import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// English translations
const enTranslations = {
  common: {
    continue: 'Continue',
    cancel: 'Cancel',
    save: 'Save',
    change: 'Change',
    refresh: 'Refresh',
    select: 'Select',
    edit: 'Edit',
    download: 'Download',
    autoStart: 'Auto-start',
    back: 'Back',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    logout: 'Logout',
    upgrade: 'Upgrade',
    next: 'Next',
    or: 'or',
    loading: 'Starting...',
    retry: 'Retry',
    savePathNew: 'Files are saved to your Pictures/Dreambooth Gallery/{{date}} folder.'
  },
  settings: {
    title: 'Device Setup',
    project: 'Project',
    credits: {
      title: 'Credits',
      refresh: 'Refresh credits',
      unlimited: 'Unlimited'
    },
    camera: {
      title: 'Camera',
      noPreview: 'No Preview Available',
      selectCamera: 'Select Camera',
      defaultCamera: 'Default',
      noDevicesFound: 'No cameras found',
      detect: 'Detect Camera...',
      troubleshoot: 'Having connection trouble? Replug the usb and refresh might help!',
      guideDescriptionPrefix: 'See ',
      guideLink: 'camera guide',
      guideDescriptionSuffix: 'for DSLR/mirrorless camera connection',
      dslrInstruction:
        'To connect to a DSLR camera, follow this installation instruction and choose "Detect Camera"',
      settings: 'Camera Settings',
      eject: 'Eject Camera',
      ejecting: 'Ejecting camera...',
      ejected: 'Camera ejected successfully',
      ejectFailed: 'Failed to eject camera',
      ejectAndRestart: 'Camera ejected, restarting app...',
      ejectDevMode: 'Camera ejected. Please restart the app manually in development mode.',
      replugRequired: 'Please replug the USB cable or refresh the app',
      driverStatus: {
        installed: 'Installed',
        uninstalled: 'Uninstalled/Occupied'
      },
      editModeOptional:
        'Camera selection is optional for edit mode. You can continue without selecting a camera.',
      instructionLink: 'Instruction',
      unknownWebcam: 'Unknown Webcam'
    },
    printer: {
      title: 'Printer',
      enablePrinting: 'Enable Printing',
      selectPrinter: 'Select Printer',
      settings: 'Printer Settings',
      notAvailable: 'Printing configuration is not available.',
      webPrintHint:
        "When enabled, the app can trigger printing (usually your browser's print dialog). Silent auto-print requires the desktop app.",
      downloadDesktop: 'Download the desktop application for auto-print configuration.',
      guideDescriptionPrefix: 'See',
      guideLink: 'printer guide',
      guideDescriptionSuffix: 'for printer setup and configuration',
      systemDefault: 'System Default',
      scaleFactor: 'Scale Factor (10-200%)',
      scaleFactorHelp: 'Scale percentage (10-200%)',
      printBackground: 'Print Background'
    },
    download: {
      title: 'Download Settings',
      saveLocal: 'Save files to local device',
      explanation:
        'When enabled, photos, GIFs, and videos will be automatically downloaded to your device.',
      savePath: 'They will be saved in the pictures/dreambooth path.',
      savePathNew: 'Files are saved to your Pictures/Dreambooth Gallery/{{date}} folder.'
    },
    pin: {
      title: 'Lock Fullscreen PIN',
      placeholder: 'PIN',
      noPin: 'No PIN set',
      change: 'Change'
    },
    autoStart: {
      title: 'Auto-Start Settings',
      withProject: 'Auto-start with this project',
      explanation: 'When enabled, the app will automatically start with this project on launch'
    },
    screensaver: {
      title: 'Screensaver',
      label: 'Enable Screensaver (Idle Mode)',
      noVideo: 'No screensaver video configured for this project. Upload one in the dashboard to use this feature.',
      usingProjectTimeout: 'Using project idle timeout:',
      seconds: 'seconds',
      configuredInDashboard: '(configured in dashboard)',
      timeout: 'Timeout (seconds):',
      explanation: "Automatically plays the project's screensaver video when idle, and disconnects the camera to save power.",
      tapToContinue: 'Tap anywhere to continue',
      controlledByProject: 'This setting is controlled by the project configuration in the dashboard.'
    },
    browserGestures: {
      title: 'Web browser',
      label: 'Reduce pull-to-refresh and overscroll navigation',
      explanation:
        'Helps kiosk-style use in mobile browsers (e.g. less pull-to-refresh and overscroll chaining). Safari edge swipe-back and some system gestures may still apply. Adding this app to the home screen (standalone) often improves behavior.'
    },
    autoLaunch: {
      title: 'Auto-Launch',
      label: 'Start app at system login',
      explanation:
        'Automatically start the app when you log into your computer (after startup, restart, or login). Does not apply to sleep/hibernate wake-up.',
      enabled: 'App will start at system login',
      disabled: 'App will not start at system login',
      error: 'Failed to update auto-launch setting'
    },
    systemControls: {
      title: 'Windows System Controls',
      description:
        'These are recommended Windows settings to optimize your photobooth experience. These settings require administrator privileges and will prompt for elevation when applying changes.',
      edgeSwipe: {
        title: 'Disable screen-edge touch swipes',
        explanation:
          'Prevents customers from accidentally exiting the photobooth app by swiping from screen edges. (A restart will be required after changing this setting.)'
      },
      lockOnWake: {
        title: 'Lock screen on wake',
        disable: 'Disable',
        enable: 'Enable',
        explanation:
          'Disables the lock screen when the computer wakes up, allowing the photobooth app to open directly without requiring a password or sign in button click'
      },
      timeouts: {
        title: 'Prevent screen off & sleep (except on battery-powered laptops)',
        enable: 'Enable',
        explanation:
          'Prevents the screen from turning off during photobooth sessions. AC power: never sleep/display off. DC power (laptops): 30 minutes timeout'
      },
      scriptDownloaded:
        'Script downloaded to Downloads folder. Right-click and select "Run with PowerShell" to apply.',
      edgeSwipesEnabled: 'Edge swipes enabled',
      lockOnWakeEnabled: 'Lock screen on wake enabled',
      applyingChanges: 'Applying changes...',
      storeInstallWarning:
        'Windows system controls require administrator privileges. If automatic execution fails, scripts will be downloaded to your Downloads folder for manual execution.'
    },
    appVersion: {
      label: 'App Version:',
      updateAvailable: 'New App Version Available! Please update to'
    },
    buttons: {
      seeAllProjects: 'See All Projects',
      editProject: 'Edit Project',
      continue: 'Continue'
    },
    pinModal: {
      change: {
        title: 'Change PIN',
        subtitle: 'Enter a 6-digit PIN which will be used to:'
      },
      set: {
        title: 'Set PIN',
        subtitle: 'Enter a new 6-digit PIN which will be used to:'
      },
      usage: {
        unlock: 'Unlock fullscreen-mode',
        skip: 'Skip payment if needed'
      }
    },
    feedback: {
      selectCamera: 'Select a camera first!',
      pinUpdated: 'PIN updated successfully'
    }
  },
  camera: {
    prompt: {
      title: 'Click to Start Camera',
      instruction: 'Click anywhere to connect your camera'
    }
  },
  nav: {
    dashboard: 'Dashboard',
    title: 'Dreambooth',
    settings: 'Settings'
  },
  login: {
    successMessage: 'Successfully logged in!',
    networkError: 'Network error. Please try again later.',
    logoAlt: 'DreamBooth Logo',
    welcomeBack: 'Welcome Back',
    signInPrompt: 'Sign in to your account',
    email: 'Email',
    password: 'Password',
    loginButton: 'Login',
    resetPasswordPrompt: 'Need to reset password or create an account?',
    visitDashboard: 'Visit the web dashboard',
    showPassword: 'Show password',
    hidePassword: 'Hide password'
  },
  projects: {
    title: 'Projects',
    subtitle: 'Manage your projects here',
    sessionCredits: 'Credits',
    refreshCredits: 'Refresh credits',
    topUpButton: 'Top Up',
    loading: 'Loading projects...',
    noProjects: 'No projects available',
    lastUpdated: 'Last Updated',
    menuOptions: 'Options',
    run: 'Run'
  },
  projectNav: {
    logoAlt: 'DreamBooth Logo',
    deviceSubscription: 'Device Subscription',
    activeSubscription: 'Active Subscription',
    planType: 'Plan Type:',
    computerName: 'Computer Name:',
    osInfo: 'OS Info:',
    activationCode: 'Activation Code:',
    changeSubscription: 'Change Subscription',
    activateSubscription: 'Activate Subscription',
    enterActivationCode: 'Enter your activation code to {{action}} this device.',
    reactivate: 'reactivate',
    activate: 'activate',
    deactivateWarning: 'Note: This will deactivate the subscription on your other device.',
    enterActivationCodePlaceholder: 'Enter your activation code',
    noCodeQuestion: "Don't have an activation code?",
    purchaseSubscription: 'Get a subscription',
    greeting: 'Hello, {{username}}!',
    subscription: 'subscription',
    failedReactivate: 'Failed to reactivate subscription',
    deviceChanged: 'Device changed successfully!',
    failedActivate: 'Failed to activate subscription',
    activatedSuccess: 'Subscription activated successfully!',
    reactivating: 'Reactivating...',
    activating: 'Activating...'
  },
  proTrial: {
    expired: 'Expired',
    timeRemainingDays: '{{days}} days left',
    timeRemainingRealtime: '{{hours}}h {{minutes}}m {{seconds}}s left'
  },
  controls: {
    settings: 'Settings',
    exitFullscreen: 'Exit Fullscreen',
    enterFullscreen: 'Enter Fullscreen',
    skipPayment: 'Skip Payment',
    exitSession: 'Exit Session',
    enterPin: 'Enter 6-digit PIN to exit fullscreen-mode',
    skipPaymentPin: 'Enter 6-digit PIN to skip payment',
    cancelSession: 'Cancel Session',
    cancelConfirm: 'Are you sure you want to cancel this session?',
    wrongPin: 'Oops! Wrong PIN. Please try again.',
    quitting: 'Ending session…',
    quittingMessage: 'Please wait while we save your session data.'
  },
  reprint: {
    title: 'Reprint',
    pinRequired: 'PIN Required',
    enterPinToAccess: 'Enter PIN to access reprint menu'
  },
  cameraSettings: {
    title: 'Camera Settings',
    appSettings: 'App Settings',
    appSettingsHint: {
      title: 'App Settings Guide',
      description:
        "These settings control how the app interacts with your camera. Adjust preview quality for better performance, set your preferred capture ratio, enable mirror mode for selfie-style preview, and choose between using the camera's physical shutter or capturing the preview frame. You can also enable/disable filter effects on the live preview."
    },
    powerSaving: {
      title: 'Power Saving Mode',
      enable: 'Enable power saving mode',
      description: 'Reduces camera load and prevents overheating'
    },
    frameRate: {
      title: 'Preview Frame Rate',
      adjust: 'Adjust frame rate',
      fps: 'FPS',
      description: 'Lower frame rates reduce camera load and heat generation'
    },
    previewQuality: {
      title: 'Preview Quality',
      options: {
        custom: 'Custom',
        pixelated: 'Pixelated (240p)',
        low: 'Low (480p)',
        medium: 'Medium (720p)',
        high: 'High (1080p)',
        ultra: 'Ultra (1440p)'
      }
    },
    captureRatio: {
      title: 'Capture Ratio',
      options: {
        auto: 'Auto',
        square: 'Square (1:1)',
        portrait: 'Portrait (3:4)',
        landscape: 'Landscape (4:3)',
        widePortrait: 'Wide Portrait (9:16)',
        wideLandscape: 'Wide Landscape (16:9)',
        classicPortrait: 'Classic Portrait (2:3)',
        classicLandscape: 'Classic Landscape (3:2)'
      }
    },
    mirror: {
      title: 'Mirror Preview',
      enable: 'Mirror camera preview'
    },
    useShutterCapture: {
      title: 'Use shutter capture',
      description:
        'Use the camera physical shutter for capturing photos. When disabled, the app will capture the current preview frame instead. Use shutter mode for better quality photos, or preview capture for faster response.',
      enable: 'Toggle shutter capture'
    },
    cameraConfig: 'Camera Configuration',
    cameraConfigWarning: {
      title: 'Camera Configuration Warning',
      description:
        'Camera settings may vary between different camera models. Some settings might not be adjustable through this software. Please test each setting to ensure it works with your camera.'
    },
    primary: 'Primary Camera',
    secondary: 'Secondary Camera (Experimental)',
    qualityPresets: {
      pixelated: 'Pixelated (240p)',
      low: 'Low (480p)',
      medium: 'Medium (720p)',
      high: 'High (1080p)',
      ultra: 'Ultra (1440p)',
      enterpriseOnly: 'Enterprise Only'
    },
    shutterSpeed: {
      '1_4': '1/4s (slowest, brightest)',
      '1_8': '1/8s',
      '1_15': '1/15s',
      '1_30': '1/30s (default)',
      '1_60': '1/60s',
      '1_125': '1/125s',
      '1_200': '1/200s (fastest, flash sync limit)'
    },
    warning: {
      blackTitle: 'Black Image Detected',
      blackImage:
        'Camera is showing a black image. Please check if the lens cover is removed or the camera is properly connected.',
      darkTitle: 'Image Too Dark',
      darkImage: 'Camera image is too dark. Please check lighting conditions or camera settings.',
      noiseTitle: 'Poor Image Quality',
      noiseImage: 'Camera image quality is poor. Please check camera connection and settings.',
      genericTitle: 'Camera Issue Detected',
      generic: 'Camera has quality issues. Please check camera settings and connection.',
      troubleshooting: 'Troubleshooting Tips:',
      tip1: 'Ensure camera lens cover is removed',
      tip2: 'Check if camera is properly connected',
      tip3: 'Verify adequate lighting in the area',
      tip4: 'Try reconnecting the camera',
      retry: 'Retry Camera',
      ignore: 'Ignore'
    }
  },
  cameraConfig: {
    label: '{label}'
  },
  cameraGuide: {
    title: "Let's setup your camera first!",
    step1: {
      title: 'Connect Your Camera',
      bullet1: 'Ensure your camera is turned ON',
      bullet2: 'Connect it to your computer via USB cable',
      bullet3: 'Wait a few seconds for Windows to detect it'
    },
    step2: {
      title: 'Install Driver',
      storeWarning:
        'Driver installation requires administrator privileges. In the Microsoft Store version, drivers must be installed manually.',
      viewManualInstructions: 'View manual installation instructions',
      electronInstruction: 'Open Zadig to install the necessary driver.',
      webInstruction: 'Download and run Zadig to install the necessary driver.',
      processing: 'Processing...',
      openZadig: 'Open Zadig',
      downloadZadig: 'Download Zadig',
      failedToOpen: 'Failed to open Zadig'
    },
    step3: {
      title: 'In Zadig',
      instruction1: 'Click Options → List All Devices',
      instruction2: 'Find your camera in the dropdown list (e.g. Canon Digital Camera)',
      instruction3: 'Click Replace Driver and wait for completion',
      instruction4: 'Close Zadig and click Done'
    },
    buttons: {
      close: 'Close',
      done: 'Done'
    }
  },
  printerGuide: {
    title: 'Printer Setup Guide',
    faq1: {
      question: 'How to enable double inch cut?',
      answer:
        'Double inch cut allows you to print two images on one sheet that can be cut in half. Ensure your printer driver is installed correctly. Open Windows Print Preferences (right-click printer in Settings > Printers & scanners > Printer preferences), configure the paper size accordingly, and adjust print settings. The app will handle the layout automatically.'
    },
    faq2: {
      question: 'What is secondary and tertiary printer?',
      answer:
        'Secondary and tertiary printers offer two purposes: Special Frames - route specific frames to dedicated printers (useful for different frame types on different printers), and Parallel Printing - distribute print jobs across multiple printers to speed up multi-print sessions.'
    },
    faq3: {
      question: 'What is remote printer?',
      answer:
        'Remote printer allows you to print from a web interface to a printer connected to your desktop app via a secure tunnel. The desktop app creates a tunnel URL that the web interface uses to send print jobs. This is useful for printing from remote locations or web-based workflows.'
    },
    buttons: {
      close: 'Close'
    }
  },
  remotePrintSettings: {
    title: 'Remote Print Settings',
    subtitle: "Override the booth's print settings for prints sent from this device.",
    noPrinter: 'Select a remote printer first.',
    useBoothDefaults: 'Use booth defaults'
  },
  printerSettings: {
    orientation: 'Orientation',
    landscape: 'Landscape',
    portrait: 'Portrait',
    pageSize: 'Page Size (microns)',
    width: 'Width',
    height: 'Height',
    dpi: 'Print Quality (DPI)',
    'dpi.horizontal': 'Horizontal',
    'dpi.vertical': 'Vertical',
    margins: 'Margins',
    'margins.default': 'Default',
    'margins.none': 'None',
    'margins.printableArea': 'Printable Area',
    'margins.custom': 'Custom',
    'margins.top': 'Top',
    'margins.bottom': 'Bottom',
    'margins.left': 'Left',
    'margins.right': 'Right',
    'margins.topTitle': 'Top margin',
    'margins.bottomTitle': 'Bottom margin',
    'margins.leftTitle': 'Left margin',
    'margins.rightTitle': 'Right margin',
    printPositionShift: 'Print Position Shift (cm)',
    testPrintInfo: {
      title: 'Test Print Configuration',
      description:
        'Use this section to test print settings and save your preferred configuration. These settings will be used for automated printing during photo sessions.'
    },
    secondaryPrinterInfo: {
      title: 'Secondary Printer Configuration',
      description:
        'Configure a secondary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
    },
    tertiaryPrinterInfo: {
      title: 'Tertiary Printer Configuration',
      description:
        'Configure a tertiary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
    },
    frameTest: {
      title: 'Frame For Print Testing',
      dragAndDrop: 'Drag and drop an image here, or click to select'
    },
    feedback: {
      selectPrinter: 'Please select a printer first',
      printInitiated: 'Printing Initialized',
      printFailed: 'Failed to print'
    },
    smartSetup: {
      loading: 'Loading printer capabilities...'
    },
    selectFrames: 'Select Frames',
    custom: 'Custom',
    noCapabilities: 'No capabilities available - configure manually',
    preset: 'Preset',
    presetHelp: 'Select a preset or choose Custom to manually configure settings',
    noCapabilitiesHelp: 'Configure settings manually in Details section below',
    details: 'Preset Details',
    printerDriverDefaults: {
      title: 'Use Printer Driver Defaults',
      description:
        'Use printer driver default settings for margins and DPI. Better compatibility with custom printer drivers.',
      toggle: 'Toggle printer driver defaults'
    },
    csharpPrinting: {
      title: 'C# Direct Printing (Experimental)',
      description: 'Use .NET printing for better reliability with certain printers',
      toggle: 'Toggle C# printing'
    },
    printerPurpose: 'Printer Purpose',
    printerPurposeHelpSecondary:
      'Select the purpose for the secondary printer: special frames for frame-based routing, or parallel for round-robin distribution.',
    printerPurposeHelpTertiary:
      'Select the purpose for the tertiary printer: special frames for frame-based routing, or parallel for round-robin distribution.',
    specialFramesPrint: 'Special frames print',
    parallelPrinting: 'Parallel Printing',
    positionShift: 'Print Position Shift (cm)',
    positionShiftHelp:
      'Fine-tune print alignment. Adjusts @page margins independently from margin settings.',
    positionShiftTop: 'Top',
    positionShiftBottom: 'Bottom',
    positionShiftLeft: 'Left',
    positionShiftRight: 'Right',
    positionShiftTopTitle: 'Top position shift',
    positionShiftBottomTitle: 'Bottom position shift',
    positionShiftLeftTitle: 'Left position shift',
    positionShiftRightTitle: 'Right position shift',
    remotePrinterStatus: {
      title: 'Remote Printer Status',
      active: 'Status: Active',
      inactive: 'Status: Inactive or not connected.',
      name: 'Name: ',
      description: 'The remote printer is active. Go to {{appUrl}}/strip to print remotely.'
    },
    preview: 'Print',
    noPreview: 'No preview available',
    upload: 'Upload Template',
    copies: 'Copies:',
    copiesTitle: 'Number of copies to print',
    printResolutionScale: {
      title: 'Print Resolution Scale',
      description:
        'Adjust the resolution of the final printed image. Higher values result in better quality but larger file sizes.'
    },
    printResultMinDisplay: {
      title: 'Minimum time on the processing screen before Result',
      description:
        'After printing and saving finish, keep the processing screen open for at least this many seconds. Use 0 to go to Result as soon as work completes. Only applies when printing is enabled and the guest is actually printing.',
      suffix: 'seconds (0 = off, max {{max}})'
    },
    defaultPrinter: 'Default Printer',
    secondaryPrinter: 'Secondary Printer',
    tertiaryPrinter: 'Tertiary Printer'
  },
  capture: {
    getReady: 'Get Ready!',
    sessionStarting: 'Session will start in',
    startNow: 'Start Now',
    orWaitCountdown: 'or wait for the countdown',
    lookAtCamera: 'Look at the Camera',
    stayStill: 'Please stay still while we capture your photo...',
    captureFailed: 'Capture Failed',
    stayStillCapturing: 'Please stay still when capturing',
    retryingMoment: 'Retrying in a moment...',
    lastCaptured: 'Last captured',
    noPhotoYet: 'No photo yet',
    recording: 'REC',
    errorStartingSessionRecorder: 'Error starting session recorder',
    errorProcessingSessionVideo: 'Error processing session video',
    selfPhotoScanQR: 'Scan to trigger from phone',
    selfPhotoQRAlt: 'Trigger from phone',
    selfPhotoEndSession: 'End session',
    selfPhotoPhotoCount: '{{count}} photos',
    selfPhotoAutoLoop: 'Auto',
    selfPhotoSessionTimer: 'Session time',
    selfPhotoEndSessionBtn: 'End Session',
    selfPhotoAutoLoopOn: 'Auto capture on',
    selfPhotoAutoLoopOff: 'Auto capture off',
    selfPhotoManualOnly: 'Manual',
    selfPhotoTriggerUrl: 'Trigger URL',
    selfPhotoTriggerNow: 'Trigger capture now',
    removePhoto: 'Remove photo'
  },
  select: {
    title: 'Arrange your photos',
    instruction: 'Tap photos to select, swipe to move, pinch to zoom',
    processingPhotos: 'Processing Photos',
    pleaseWait: 'Please wait while we process your photos...',
    printPhotos: 'Print Photos',
    swipeInstructions: 'Swipe to move, pinch to zoom',
    mirror: 'Mirror',
    selectionLocked: 'Selection locked',
    processing: 'Processing...',
    processingVideo: 'Processing Video...',
    next: 'Next ({{count}}/{{max}})',
    capture: 'Capture',
    selected: 'Selected',
    clickToSelect: 'Click to Select',
    continue: 'Continue',
    errorGif: 'Error creating animated GIF',
    stripExportFailed: 'Failed to create strip preview',
    errorVideo: 'Error processing video',
    errorProcessing: 'There was an error processing your photos. Please try again.',
    selectPhotos: 'Select {{count}}/{{max}} photos',
    exportFailed: 'Failed to generate strip. Continuing without frame...',
    exportTimeout: 'Strip generation is taking longer than expected. Please wait...'
  },
  retake: {
    title: 'Not Happy with Some Photos?',
    headline: 'Tap photos to retake',
    maxHint: 'Max {{count}}',
    pillSessions: '{{count}} left · Session {{current}} of {{max}}',
    remainingSessions: 'Retakes left: {{count}}',
    selectUpTo: 'Select up to',
    photos: 'photos',
    toRetake: 'to retake',
    selectToRetake: 'Select the photos you want to retake',
    sessionCount: 'Retake session {{current}} of {{max}}',
    photoAlt: 'Photo {{number}}',
    photoSelectedAlt: 'Photo {{number}} selected for retake',
    overlayRetake: 'Retake',
    overlaySelected: 'Selected',
    selectedToRetake: 'Selected to Retake',
    clickToRetake: 'Click to Retake',
    retakeSelected: 'Retake Selected Photos ({{count}})',
    retakeCount: 'Retake ({{count}})',
    retakeAll: 'Retake All',
    keepAll: 'Keep all',
    explanation: 'Selected photos will be deleted and you will take new ones. This cannot be undone.',
    confirmContinue: 'Continue with all photos?',
    confirmContinueHint: 'You can go back to retake any photo, or continue to the next step.',
    confirmKeepAllTitle: 'Are you good with all the photos?',
    confirmKeepAllMessage: 'You can choose to retake some photos or continue to the next step.',
    back: 'Back',
    continue: 'Continue',
    confirmKeepAllNo: 'No, I want to retake my photos',
    confirmKeepAllYes: 'Yes, I want to continue',
    confirmingKeepAll: 'Continuing...'
  },
  result: {
    videoReady: 'Your Video Is Ready!',
    photosReady: 'Your Photos Are Ready!',
    scanQrInstruction: 'Scan the QR code below to access your {{type}}',
    video: 'video',
    digitalPhotos: 'digital photos',
    photoStripAlt: 'Photo Strip',
    qrCodeAlt: 'QR Code',
    sessionId: 'Session ID:',
    scanToView: 'Scan to view gallery',
    videoRecording: 'Video Recording',
    photoStrip: 'Photo Strip',
    backToHome: 'Back to Home',
    satisfaction: {
      question: 'How was your photobooth experience?',
      rateExperience: 'Rate your experience',
      thankYou: 'Thank you for your feedback!',
      starAlt: 'Rate {{rating}} stars'
    },
    emailGallery: {
      title: 'Email your gallery link',
      description: "We'll send one message with a link to this gallery.",
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      sendEmail: 'Send',
      sendingEmail: 'Sending…',
      invalidEmail: 'Enter a valid email address.',
      shareSuccess: 'Sent! Check your inbox.',
      shareError: 'Could not send. Try again.',
      shareNetworkError: 'Connection problem. Try again.',
      close: 'Close'
    },
    postSessionAi: {
      title: 'AI enhancement',
      subtitle: 'Optional polish for your photos.',
      guestPromptLabel: 'Add a note (optional)',
      guestPromptPlaceholder: 'e.g. more vintage',
      skip: 'Skip',
      apply: 'Apply AI',
      applying: 'Applying…',
      continue: 'Continue',
      errorGeneric: 'Something went wrong. Tap Skip to continue.',
      insufficientCredits: 'AI is unavailable right now. Tap Skip to continue.',
      missingStrip: 'Strip not ready yet. Tap Skip to continue.',
      missingRaw: 'No photos available for AI. Tap Skip to continue.',
      missingRawNeedHttps: 'These photos are still uploading. Tap Skip to continue.',
      timeout: 'AI is taking longer than usual. Tap Skip to continue.',
      offline: 'AI needs an internet connection. Tap Skip to continue.',
      stripUploading: 'Finishing the strip upload…',
      previewBefore: 'Before',
      previewAfter: 'After',
      previewCompareHeading: 'Before and after preview',
      previewLoading: 'Loading preview…',
      previewMissingUrls: 'No preview yet.',
      previewLoadError: 'Could not load preview. Try again.',
      previewImageLoadError: 'Could not load preview. Try again.',
      filmstripAriaLabel: 'Your session photos',
      filmstripCaption: 'Session photos',
      filmstripHelper: 'Tap a photo to generate AI mode out of it.',
      filmstripThumb: 'Session photo',
      filmstripThumbWithAi: 'Session photo (AI ready)',
      aiProcessedTooltip: 'AI version ready',
      dragToCompareHint: 'Drag to compare before and after.',
      previewEmptyMessage: 'No comparison preview yet. You can still apply AI below.',
      pickOneSpecial: 'Pick 1 special photo to enhance with AI.',
      rawOnce: 'AI can enhance one photo per session.',
      rawUsedBadge: 'AI used'
    }
  },
  filter: {
    nextFilter: 'Next filter',
    prevFilter: 'Previous filter'
  },
  frame: {
    framesListbox: 'Frames',
    previewAlt: 'Preview'
  },
  payment: {
    skipGuide:
      'Need to skip payment? Click the money icon in the top right corner and enter the PIN.',
    qrisLogoAlt: 'QRIS logo',
    qrCodeAlt: 'QR Code',
    automaticCheck: 'Payment status will be checked automatically.',
    expired: 'Payment code expired',
    restartPrompt: 'Click to restart transaction',
    restart: 'Restart Transaction',
    successMessage: 'Payment successful!',
    notSuccessful: 'Payment not successful. Please try again.',
    offline: 'No internet connection',
    offlineMessage: 'Please check your internet connection and try again',
    retry: 'Retry Payment',
    reload: 'Reload',
    checkingConnection: 'Checking connection...',
    networkError: 'Network error occurred',
    snapError: 'Payment failed. Please try again.',
    wrongCode: 'Incorrect code. Please try again.',
    enterCode: 'Enter Code to Continue',
    offlineMode: 'Offline Mode - No Internet',
    autoRestart: 'Auto-restart in',
    snapReady: 'Payment gateway ready',
    snapOpening: 'Opening payment...',
    openSnap: 'Open Payment',
    payNowTitle: 'PAY TO START SESSION',
    codeEntry: {
      title: 'Enter Access Code',
      subtitle: 'Enter 6-digit code to bypass payment in offline mode'
    }
  },
  checkout: {
    title: 'Review Your Order',
    print: 'Print',
    choosePrints: 'Choose number of prints',
    minus: 'Minus',
    plus: 'Plus',
    gif: 'GIF',
    getAnimated: 'Get animated image',
    recording: 'Recording',
    getRecording: 'Get photo session recording',
    video: 'Video',
    recordVideo: 'Record a video',
    free: 'Free',
    subtotal: 'Subtotal:',
    discount: 'Discount:',
    total: 'Total:',
    remainingTime: 'Remaining Time:',
    havePromo: 'Have a voucher code?',
    proceedToPayment: 'Proceed to Payment',
    enterPromo: 'Enter promo code',
    voucherCodeTitle: 'Voucher code',
    voucherCodePlaceholder: 'Type or paste your code',
    promoApplied: 'Promo code applied successfully!',
    invalidPromo: 'Invalid promo code',
    cancel: 'Cancel',
    apply: 'Apply',
    promoValidationFailed: 'Failed to validate promo code. Please try again.',
    digital: 'Digital Photos',
    getDigital: 'Get digital copies of your photos',
    keyboard: 'Keyboard',
    scanQR: 'Scan QR',
    scanningQR: 'Scanning for QR code...',
    qrInstructions:
      'Hold a QR code in front of your camera. The promo code will be automatically detected and applied.',
    qrScannerActive: 'QR Scanner Active',
    pointCamera: 'Point your camera at a QR code containing the promo code',
    failedToCaptureImage: 'Failed to capture image from camera',
    errorScanningQR: 'Error scanning QR code',
    addNumberOfPeople: 'Please select the number of people.',
    choosePeople: 'Choose number of people',
    pax: 'Number of People'
  },
  bahela: {
    welcome: {
      title: 'Welcome',
      subtitle: 'LADIES AND GENTLEMEN!',
      promoButton: 'Promo Code',
      startButton: 'Start Session',
      tutorial: 'Tutorial',
      noCredits: 'No credits available. Please top up your credits.',
      startError: 'Failed to start session. Please try again.',
      discountApplied: 'Discount is already applied, please start the session!',
      promoValidationFailed: 'Gagal memvalidasi kode promo. Silakan coba lagi.',
      digital: 'Foto Digital',
      getDigital: 'Dapatkan salinan digital foto Anda',
      next: 'Selanjutnya',
      or: 'atau',
      savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
    },
    promoModal: {
      title: 'Enter Promo Code',
      placeholder: 'Promo Code',
      submit: 'Submit',
      cancel: 'Cancel',
      promoExpired: 'Promo code has expired',
      invalidPromo: 'Invalid promo code',
      percentageDiscountApplied: "{{value}}% discount applied, let's start the session!",
      fixedDiscountApplied: "{{value}} discount applied, let's start the session!",
      promoError: 'Error validating promo code',
      cashVoucherApplied: 'Cash voucher applied.',
      customFrameVoucherApplied:
        'Custom frame voucher applied. Your frame will be selected at the kiosk.'
    },
    tutorial: {
      title: 'BAHeLA Photobooth Tutorial'
    }
  },
  recording: {
    title: 'Recording',
    type: 'Recording Type',
    modes: {
      live: 'Live Mode',
      story: 'Story Mode',
      session: 'Session Mode'
    },
    description: {
      recording: 'Live mode video of the photo strip',
      strip_live: 'Live mode video the photo strip',
      strip_story: 'Story mode video the photo strip',
      session_recording: 'Recording of the entire photo session'
    },
    toggle: 'Toggle recording package'
  },
  view: {
    noCreditsModal: {
      title: 'Subscription or credits needed',
      description:
        "Your subscription has ended or you have no credits left. Renew in the dashboard or add credits to continue using the app without interruption.",
      purchaseSubscription: 'Activate Subscription',
      topUpCredits: 'Top Up Credits',
      close: 'Close'
    },
    subscriptionValidUntil: 'Your subscription is valid until {date}.'
  },
  consent: {
    title: 'Photo Publication Consent',
    subtitle: "We'd love to showcase your amazing photos!",
    question: 'Can we use your photos for publication?',
    description:
      "Your beautiful photos could be featured on our website, social media, or marketing materials to inspire others. We'll never share your personal information.",
    yourPhotos: 'Your Photos',
    yourVideo: 'Your Video',
    photoStripAlt: 'Photo Strip',
    benefits: {
      title: 'What this means:',
      featured: 'May be featured on our social media or website',
      inspire:
        'Let your unique style and creativity inspire others to try our photobooth experience',
      privacy: 'No personal information will be shared publicly'
    },
    accept: "Yes, I'd love to!",
    decline: 'No, thanks',
    footer:
      'You can change this preference anytime by contacting our support team at dreamboothstudio.com.',
    newspaper: {
      headline: 'READY TO BE FAMOUS?'
    }
  },
  onboarding: {
    congratulations: {
      title: 'Congratulations!',
      subtitle: "You've successfully completed your first photobooth session!",
      nextSteps: "Here's what you can do next:",
      customize: 'Customize Your Project',
      analytics: 'View Analytics',
      gallery: 'Browse Gallery',
      payment: 'Manage Subscription',
      getStarted: 'Get Started'
    }
  },
  newspaper: {
    welcome: {
      headline: 'Newspaper Photobooth: {{content}}',
      takePhoto: 'Take Photo',
      subheadline: "{{city}}'s Most Beautiful {{year}}",
      subheadlineDefault: "World's Most Beautiful {{year}}",
      introWithCity: 'Citizens from {{city}} have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and local heritage.',
      introDefault: 'Local citizens have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and shared moments.',
      aboutCity: 'About {{city}}',
      photoboothSection: 'Photobooth That Brings It All',
      dreamboothSection: 'Creativity Unleashed By Innovative Software'
    },
    checkout: {
      steps: {
        title: 'How to Use',
        step1: 'Step 1: Review your order and select packages',
        step2: 'Step 2: Complete payment if required',
        step3: 'Step 3: Choose your filter style',
        step4: 'Step 4: Select your photo frame',
        step5: 'Step 5: Take your photos',
        step6: 'Step 6: Review and retake if needed',
        step7: 'Step 7: Get your photos!'
      }
    },
    filter: {
      timeRemaining: 'Time Remaining'
    },
    select: {
      timeRemaining: 'Time Remaining'
    }
  },
  strip: {
    step1Title: 'Step 1: Select Strip',
    step1Desc: 'Choose a frame and upload photos if required.',
    step2Title: 'Step 2: Upload Configuration',
    step2Desc: 'Choose whether to upload to gallery and configure project/session.',
    step3Title: 'Step 3: Remote Printing Configuration',
    step3Desc: 'Select a remote printer and configure print settings.',
    step4Title: 'Step 4: Preview & Generate',
    step4Desc: 'Review your configuration and generate the strip.',
    modeConfig: 'Create New',
    modeUpload: 'Upload existing photo strip',
    useExistingStrip: 'Use existing photo strip',
    chooseFromGallery: 'Choose from gallery',
    selectFrame: 'Please select a frame',
    selectFrameTitle: 'Select Photo Strip Frame',
    uploadStripImage: 'Please upload a photo strip image',
    uploadPhotos: 'Click to upload photos',
    uploadHint: 'Select up to {{count}} photos',
    remove: 'Click to Remove',
    mirror: 'Mirror',
    uploadToGallery: 'Upload to gallery',
    selectProjectTitle: 'Select Project',
    enterSessionId: 'Enter Session ID',
    validatingSession: 'Validating session...',
    sessionValid: 'Session ID valid',
    sessionInvalid: 'Session ID invalid or not authorized',
    printStrip: 'Print strip',
    selectRemotePrinterTitle: 'Select Remote Printer',
    noPrinterSelected: 'No printer selected.',
    preview: 'Preview',
    previewHint: 'Preview of your photo strip',
    generating: 'Generating...',
    generate: 'Generate Strip',
    downloading: 'Downloading...',
    uploading: 'Uploading...',
    printing: 'Printing...',
    errorFetchingData: 'Error fetching data',
    errorFetchingPrinters: 'Failed to fetch remote printers',
    exportFailed: 'Failed to create strip preview',
    noUser: 'User not authenticated',
    invalidSessionId: 'Not authorized to upload to this gallery',
    uploadTimeout: 'Upload is taking longer than expected. Continuing...',
    uploadSuccess: 'Strip uploaded successfully',
    uploadFailed: 'Failed to upload strip - Invalid Session ID',
    printSuccess: 'Print job sent successfully!',
    printFailed: 'Print failed: {{error}}',
    base64Error: 'Failed to prepare image for printing.',
    printError: 'Error sending print job.',
    generationError: 'Error generating photo strip',
    uploaded: 'Uploaded:',
    step2: {
      upload: 'Upload',
      skip: 'Skip'
    },
    step3: {
      print: 'Print',
      done: 'Done'
    },
    galleryModal: {
      title: 'Select Photos from Gallery',
      selectUpTo: 'Select up to {{count}} photos',
      loading: 'Loading photos...',
      loadingMore: 'Loading more photos...',
      noPhotos: 'No photos found',
      errorFetching: 'Failed to fetch photos',
      maxSelected: 'You can only select up to {{count}} photos',
      noSelection: 'Please select at least one photo'
    }
  }
}

// Indonesian translations
const idTranslations = {
  common: {
    continue: 'Lanjutkan',
    cancel: 'Batal',
    save: 'Simpan',
    change: 'Ubah',
    refresh: 'Muat ulang',
    select: 'Pilih',
    edit: 'Edit',
    download: 'Unduh',
    autoStart: 'Mulai Otomatis',
    back: 'Kembali',
    yes: 'Ya',
    no: 'Tidak',
    close: 'Tutup',
    logout: 'Keluar',
    upgrade: 'Tingkatkan',
    next: 'Selanjutnya',
    or: 'atau',
    loading: 'Memulai...',
    retry: 'Coba Lagi',
    savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
  },
  settings: {
    title: 'Pengaturan Perangkat',
    project: 'Proyek',
    credits: {
      title: 'Kredit',
      refresh: 'Muat ulang kredit',
      unlimited: 'Tidak Terbatas'
    },
    camera: {
      title: 'Kamera',
      noPreview: 'Tidak Ada Pratinjau',
      selectCamera: 'Pilih Kamera',
      defaultCamera: 'Default',
      secondaryTitle: 'Kamera Kedua',
      noDevicesFound: 'Tidak ada kamera yang ditemukan',
      detect: 'Deteksi Kamera...',
      troubleshoot:
        'Mengalami masalah koneksi? Cabut dan tancapkan kembali USB dan muat ulang mungkin membantu!',
      guideDescriptionPrefix: 'Lihat ',
      guideLink: 'panduan kamera',
      guideDescriptionSuffix: 'untuk koneksi kamera DSLR/mirrorless',
      dslrInstruction:
        'Untuk terhubung ke kamera DSLR, ikuti petunjuk instalasi ini dan pilih "Deteksi Kamera"',
      settings: 'Pengaturan Kamera',
      eject: 'Lepas Kamera',
      ejecting: 'Melepas kamera...',
      ejected: 'Kamera berhasil dilepas',
      ejectFailed: 'Gagal melepas kamera',
      ejectAndRestart: 'Kamera dilepas, memulai ulang aplikasi...',
      ejectDevMode:
        'Kamera dilepas. Silakan mulai ulang aplikasi secara manual dalam mode pengembangan.',
      replugRequired: 'Silakan cabut dan pasang kembali kabel USB atau muat ulang aplikasi',
      warning: {
        blackTitle: 'Gambar Hitam Terdeteksi',
        blackImage:
          'Kamera menampilkan gambar hitam. Silakan periksa apakah penutup lensa sudah dilepas atau kamera terhubung dengan benar.',
        darkTitle: 'Gambar Terlalu Gelap',
        darkImage:
          'Gambar kamera terlalu gelap. Silakan periksa kondisi pencahayaan atau pengaturan kamera.',
        noiseTitle: 'Kualitas Gambar Buruk',
        noiseImage: 'Kualitas gambar kamera buruk. Silakan periksa koneksi kamera dan pengaturan.',
        genericTitle: 'Masalah Kamera Terdeteksi',
        generic: 'Kamera memiliki masalah kualitas. Silakan periksa pengaturan kamera dan koneksi.',
        troubleshooting: 'Tips Troubleshooting:',
        tip1: 'Pastikan penutup lensa kamera sudah dilepas',
        tip2: 'Periksa apakah kamera terhubung dengan benar',
        tip3: 'Verifikasi pencahayaan yang memadai di area',
        tip4: 'Coba hubungkan kembali kamera',
        retry: 'Coba Lagi Kamera',
        ignore: 'Abaikan'
      }
    },
    printer: {
      title: 'Printer',
      enablePrinting: 'Aktifkan Pencetakan',
      selectPrinter: 'Pilih Printer',
      settings: 'Pengaturan Printer',
      notAvailable: 'Konfigurasi pencetakan tidak tersedia.',
      webPrintHint:
        'Jika diaktifkan, aplikasi dapat memicu pencetakan (biasanya dialog cetak browser). Cetak otomatis tanpa dialog memerlukan aplikasi desktop.',
      downloadDesktop: 'Unduh aplikasi desktop untuk konfigurasi cetak otomatis.',
      guideDescriptionPrefix: 'Lihat',
      guideLink: 'panduan printer',
      guideDescriptionSuffix: 'untuk pengaturan dan konfigurasi printer',
      systemDefault: 'System Default',
      scaleFactor: 'Faktor Skala (10-200%)',
      scaleFactorHelp: 'Persentase skala (10-200%)',
      printBackground: 'Cetak Latar Belakang'
    },
    download: {
      title: 'Pengaturan Unduhan',
      saveLocal: 'Simpan file ke perangkat lokal',
      explanation:
        'Saat diaktifkan, foto, GIF, dan video akan secara otomatis diunduh ke perangkat Anda.',
      savePath: 'File akan disimpan di jalur pictures/dreambooth.',
      savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
    },
    pin: {
      title: 'PIN Kunci Layar Penuh',
      placeholder: 'PIN',
      noPin: 'PIN belum diatur',
      change: 'Ubah'
    },
    autoStart: {
      title: 'Pengaturan Mulai Otomatis',
      withProject: 'Mulai otomatis dengan proyek ini',
      explanation:
        'Saat diaktifkan, aplikasi akan otomatis memulai dengan proyek ini saat diluncurkan'
    },
    screensaver: {
      title: 'Screensaver',
      label: 'Aktifkan Screensaver (Mode Idle)',
      noVideo: 'Tidak ada video screensaver yang dikonfigurasi untuk proyek ini. Unggah video di dashboard untuk menggunakan fitur ini.',
      usingProjectTimeout: 'Menggunakan timeout idle proyek:',
      seconds: 'detik',
      configuredInDashboard: '(dikonfigurasi di dashboard)',
      timeout: 'Timeout (detik):',
      explanation: 'Secara otomatis memutar video screensaver proyek saat idle, dan memutuskan kamera untuk menghemat daya.',
      tapToContinue: 'Ketuk di mana saja untuk melanjutkan',
      controlledByProject: 'Pengaturan ini dikontrol oleh konfigurasi proyek di dashboard.'
    },
    browserGestures: {
      title: 'Browser web',
      label: 'Kurangi pull-to-refresh dan navigasi overscroll',
      explanation:
        'Membantu mode kiosk di browser mobile (mis. lebih sedikit pull-to-refresh dan overscroll). Swipe tepi Safari dan beberapa gestur sistem mungkin tetap aktif. Menambahkan aplikasi ke layar beranda (standalone) sering memperbaiki perilaku.'
    },
    autoLaunch: {
      title: 'Auto-Launch',
      label: 'Start app at system login',
      explanation:
        'Secara otomatis memulai aplikasi saat Anda masuk ke komputer (setelah startup, restart, atau login). Tidak berlaku untuk bangun dari sleep/hibernate.',
      enabled: 'App will start at system login',
      disabled: 'App will not start at system login',
      error: 'Gagal memperbarui pengaturan auto-launch'
    },
    systemControls: {
      title: 'Windows System Controls',
      description:
        'Pengaturan Windows yang direkomendasikan untuk mengoptimalkan pengalaman photobooth Anda. Pengaturan ini memerlukan hak administrator dan akan meminta elevasi saat menerapkan perubahan.',
      edgeSwipe: {
        title: 'Disable screen-edge touch swipes',
        explanation:
          'Mencegah pelanggan secara tidak sengaja keluar dari aplikasi photobooth dengan swipe dari tepi layar. Perubahan ini memerlukan restart Windows agar berlaku.'
      },
      lockOnWake: {
        title: 'Lock screen on wake',
        disable: 'Nonaktifkan',
        enable: 'Aktifkan',
        explanation:
          'Menonaktifkan lock screen saat komputer bangun, memungkinkan aplikasi photobooth terbuka langsung tanpa memerlukan password atau klik sign in'
      },
      timeouts: {
        title: 'Cegah layar mati & mode sleep (kecuali laptop pada mode baterai)',
        enable: 'Aktifkan',
        explanation:
          'Mencegah layar mati selama sesi photobooth. Daya AC: tidak pernah sleep/display off. Daya DC (laptop): timeout 30 menit'
      },
      scriptDownloaded:
        'Skrip diunduh ke folder Downloads. Klik kanan dan pilih "Run with PowerShell" untuk menerapkan.',
      edgeSwipesEnabled: 'Edge swipes diaktifkan',
      lockOnWakeEnabled: 'Lock screen on wake diaktifkan',
      applyingChanges: 'Menerapkan perubahan...',
      storeInstallWarning:
        'Windows system controls memerlukan hak administrator. Jika eksekusi otomatis gagal, skrip akan diunduh ke folder Downloads untuk eksekusi manual.'
    },
    appVersion: {
      label: 'Versi Aplikasi:',
      updateAvailable: 'Versi Aplikasi Baru Tersedia! Silakan perbarui ke'
    },
    buttons: {
      seeAllProjects: 'Lihat Semua Proyek',
      editProject: 'Edit Proyek',
      continue: 'Lanjutkan'
    },
    pinModal: {
      change: {
        title: 'Ubah PIN',
        subtitle: 'Masukkan PIN 6-digit yang akan digunakan untuk:'
      },
      set: {
        title: 'Atur PIN',
        subtitle: 'Masukkan PIN 6-digit baru yang akan digunakan untuk:'
      },
      usage: {
        unlock: 'Membuka kunci mode layar penuh',
        skip: 'Melewati pembayaran jika diperlukan'
      }
    },
    feedback: {
      selectCamera: 'Pilih kamera terlebih dahulu!',
      pinUpdated: 'PIN berhasil diperbarui'
    }
  },
  camera: {
    prompt: {
      title: 'Klik untuk Memulai Kamera',
      instruction: 'Klik di mana saja untuk menghubungkan kamera Anda'
    }
  },
  nav: {
    dashboard: 'Dasbor',
    title: 'Dreambooth',
    settings: 'Pengaturan'
  },
  login: {
    successMessage: 'Berhasil masuk!',
    networkError: 'Kesalahan jaringan. Silakan coba lagi nanti.',
    logoAlt: 'Logo DreamBooth',
    welcomeBack: 'Selamat Datang Kembali',
    signInPrompt: 'Masuk ke akun Anda',
    email: 'Email',
    password: 'Kata Sandi',
    loginButton: 'Masuk',
    resetPasswordPrompt: 'Perlu mengatur ulang kata sandi atau membuat akun?',
    visitDashboard: 'Kunjungi dasbor web',
    showPassword: 'Tampilkan kata sandi',
    hidePassword: 'Sembunyikan kata sandi'
  },
  projects: {
    title: 'Proyek',
    subtitle: 'Kelola proyek Anda di sini',
    sessionCredits: 'Kredit',
    refreshCredits: 'Muat ulang kredit',
    topUpButton: 'Isi Ulang',
    loading: 'Memuat proyek...',
    noProjects: 'Tidak ada proyek tersedia',
    lastUpdated: 'Terakhir Diperbarui',
    menuOptions: 'Opsi',
    run: 'Jalankan'
  },
  projectNav: {
    logoAlt: 'Logo DreamBooth',
    deviceSubscription: 'Langganan Perangkat',
    activeSubscription: 'Langganan Aktif',
    planType: 'Jenis Paket:',
    computerName: 'Nama Komputer:',
    osInfo: 'Info OS:',
    activationCode: 'Kode Aktivasi:',
    changeSubscription: 'Ubah Langganan',
    activateSubscription: 'Aktifkan Langganan',
    enterActivationCode: 'Masukkan kode aktivasi Anda untuk {{action}} perangkat ini.',
    reactivate: 'Aktifkan Kembali',
    activate: 'Aktifkan',
    deactivateWarning: 'Catatan: Ini akan menonaktifkan langganan di perangkat lain Anda.',
    enterActivationCodePlaceholder: 'Masukkan kode aktivasi Anda',
    noCodeQuestion: 'Tidak punya kode aktivasi?',
    purchaseSubscription: 'Dapatkan kode langganan',
    greeting: 'Halo, {{username}}!',
    subscription: 'langganan',
    failedReactivate: 'Gagal mengaktifkan kembali langganan',
    deviceChanged: 'Perangkat berhasil diubah!',
    failedActivate: 'Gagal mengaktifkan langganan',
    activatedSuccess: 'Langganan berhasil diaktifkan!',
    reactivating: 'Mengaktifkan kembali...',
    activating: 'Mengaktifkan...'
  },
  proTrial: {
    expired: 'Kadaluarsa',
    timeRemainingDays: '{{days}} hari tersisa',
    timeRemainingRealtime: '{{hours}}j {{minutes}}m {{seconds}}d tersisa'
  },
  controls: {
    settings: 'Pengaturan',
    exitFullscreen: 'Keluar Layar Penuh',
    enterFullscreen: 'Masuk Layar Penuh',
    skipPayment: 'Lewati Pembayaran',
    exitSession: 'Keluar Sesi',
    enterPin: 'Masukkan PIN 6-digit untuk keluar dari mode layar penuh',
    skipPaymentPin: 'Masukkan PIN 6-digit untuk melewati pembayaran',
    cancelSession: 'Batalkan Sesi',
    cancelConfirm: 'Kembali ke Beranda?',
    wrongPin: 'Oops! PIN salah. Silakan coba lagi.',
    quitting: 'Mengakhiri sesi…',
    quittingMessage: 'Mohon tunggu sementara kami menyimpan data sesi Anda.'
  },
  reprint: {
    title: 'Cetak Ulang',
    pinRequired: 'PIN Diperlukan',
    enterPinToAccess: 'Masukkan PIN untuk mengakses menu cetak ulang'
  },
  cameraSettings: {
    title: 'Pengaturan Kamera',
    appSettings: 'Pengaturan Aplikasi',
    appSettingsHint: {
      title: 'Panduan Pengaturan Aplikasi',
      description:
        'Pengaturan ini mengontrol bagaimana aplikasi berinteraksi dengan kamera Anda. Sesuaikan kualitas pratinjau untuk performa lebih baik, atur rasio pengambilan yang diinginkan, aktifkan mode cermin untuk pratinjau gaya selfie, dan pilih antara menggunakan "shutter" fisik kamera atau menangkap tangkapan layar pratinjau. Anda juga dapat mengaktifkan/menonaktifkan efek filter pada pratinjau langsung.'
    },
    powerSaving: {
      title: 'Mode Hemat Daya',
      enable: 'Aktifkan mode hemat daya',
      description: 'Mengurangi beban kamera dan mencegah panas berlebih'
    },
    frameRate: {
      title: 'Kecepatan Frame Pratinjau',
      adjust: 'Sesuaikan kecepatan frame',
      fps: 'FPS',
      description:
        'Kecepatan frame yang lebih rendah mengurangi beban kamera dan panas yang dihasilkan'
    },
    previewQuality: {
      title: 'Kualitas Pratinjau',
      options: {
        custom: 'Custom',
        pixelated: 'Pixelated (240p)',
        low: 'Low (480p)',
        medium: 'Medium (720p)',
        high: 'High (1080p)',
        ultra: 'Ultra (1440p)'
      }
    },
    captureRatio: {
      title: 'Capture Ratio',
      options: {
        auto: 'Auto',
        square: 'Square (1:1)',
        portrait: 'Portrait (3:4)',
        landscape: 'Landscape (4:3)',
        widePortrait: 'Wide Portrait (9:16)',
        wideLandscape: 'Wide Landscape (16:9)',
        classicPortrait: 'Classic Portrait (2:3)',
        classicLandscape: 'Classic Landscape (3:2)'
      }
    },
    mirror: {
      title: 'Cermin Pratinjau',
      enable: 'Cerminkan pratinjau kamera'
    },
    useShutterCapture: {
      title: 'Gunakan shutter capture',
      description:
        'Gunakan shutter fisik kamera untuk mengambil foto. Saat dinonaktifkan, aplikasi akan menangkap tangkapan layar pratinjau saat ini. Gunakan mode shutter untuk foto berkualitas lebih baik, atau tangkapan pratinjau untuk respons lebih cepat.',
      enable: 'Alihkan shutter capture'
    },
    cameraConfig: 'Konfigurasi Kamera',
    cameraConfigWarning: {
      title: 'Peringatan Konfigurasi Kamera',
      description:
        'Pengaturan kamera dapat bervariasi antar model kamera. Beberapa pengaturan mungkin tidak dapat disesuaikan melalui perangkat lunak ini. Please test each setting to ensure it works with your camera.'
    },
    primary: 'Kamera Utama',
    secondary: 'Kamera Kedua (Eksperimental)',
    qualityPresets: {
      pixelated: 'Pixelated (240p)',
      low: 'Low (480p)',
      medium: 'Medium (720p)',
      high: 'High (1080p)',
      ultra: 'Ultra (1440p)',
      enterpriseOnly: 'Enterprise Only'
    },
    shutterSpeed: {
      '1_4': '1/4s (paling lambat, paling terang)',
      '1_8': '1/8s',
      '1_15': '1/15s',
      '1_30': '1/30s (default)',
      '1_60': '1/60s',
      '1_125': '1/125s',
      '1_200': '1/200s (paling cepat, batas flash sync)'
    }
  },
  cameraConfig: {
    label: '{label}'
  },
  cameraGuide: {
    title: 'Mari siapkan kamera Anda terlebih dahulu!',
    step1: {
      title: 'Hubungkan Kamera Anda',
      bullet1: 'Pastikan kamera Anda dalam keadaan ON',
      bullet2: 'Hubungkan ke komputer Anda melalui kabel USB',
      bullet3: 'Tunggu beberapa detik agar Windows mendeteksinya'
    },
    step2: {
      title: 'Instal Driver',
      storeWarning:
        'Instalasi driver memerlukan hak administrator. Dalam versi Microsoft Store, driver harus diinstal secara manual.',
      viewManualInstructions: 'Lihat instruksi instalasi manual',
      electronInstruction: 'Buka Zadig untuk menginstal driver yang diperlukan.',
      webInstruction: 'Unduh dan jalankan Zadig untuk menginstal driver yang diperlukan.',
      processing: 'Memproses...',
      openZadig: 'Buka Zadig',
      downloadZadig: 'Unduh Zadig',
      failedToOpen: 'Gagal membuka Zadig'
    },
    step3: {
      title: 'Di Zadig',
      instruction1: 'Klik Options → List All Devices',
      instruction2: 'Temukan kamera Anda di daftar dropdown (mis. Canon Digital Camera)',
      instruction3: 'Klik Replace Driver dan tunggu hingga selesai',
      instruction4: 'Tutup Zadig dan klik Done'
    },
    buttons: {
      close: 'Tutup',
      done: 'Selesai'
    }
  },
  printerGuide: {
    title: 'Panduan Pengaturan Printer',
    faq1: {
      question: 'Bagaimana cara mengaktifkan double inch cut?',
      answer:
        'Double inch cut memungkinkan Anda mencetak dua gambar pada satu lembar yang dapat dipotong menjadi dua. Pastikan driver printer Anda terpasang dengan benar. Buka Windows Print Preferences (klik kanan printer di Settings > Printers & scanners > Printer preferences), konfigurasi ukuran kertas sesuai kebutuhan, dan sesuaikan pengaturan cetak. Aplikasi akan menangani tata letak secara otomatis.'
    },
    faq2: {
      question: 'Apa itu printer sekunder dan tersier?',
      answer:
        'Printer sekunder dan tersier menawarkan dua tujuan: Special Frames - mengarahkan frame tertentu ke printer khusus (berguna untuk jenis frame berbeda di printer berbeda), dan Parallel Printing - mendistribusikan pekerjaan cetak ke beberapa printer untuk mempercepat sesi multi-cetak.'
    },
    faq3: {
      question: 'Apa itu remote printer?',
      answer:
        'Remote printer memungkinkan Anda mencetak dari antarmuka web ke printer yang terhubung ke aplikasi desktop Anda melalui tunnel yang aman. Aplikasi desktop membuat URL tunnel yang digunakan antarmuka web untuk mengirim pekerjaan cetak. Ini berguna untuk mencetak dari lokasi jarak jauh atau alur kerja berbasis web.'
    },
    buttons: {
      close: 'Tutup'
    }
  },
  remotePrintSettings: {
    title: 'Pengaturan Cetak Jarak Jauh',
    subtitle: 'Ganti pengaturan cetak booth untuk cetakan yang dikirim dari perangkat ini.',
    noPrinter: 'Pilih printer jarak jauh terlebih dahulu.',
    useBoothDefaults: 'Gunakan pengaturan booth'
  },
  printerSettings: {
    orientation: 'Orientasi',
    landscape: 'Lanskap',
    portrait: 'Potret',
    pageSize: 'Ukuran Halaman (mikron)',
    width: 'Lebar',
    height: 'Tinggi',
    printPositionShift: 'Pergeseran Posisi Cetak (cm)',
    testPrintInfo: {
      title: 'Test Print Configuration',
      description:
        'Use this section to test print settings and save your preferred configuration. These settings will be used for automated printing during photo sessions.'
    },
    secondaryPrinterInfo: {
      title: 'Secondary Printer Configuration',
      description:
        'Configure a secondary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
    },
    tertiaryPrinterInfo: {
      title: 'Konfigurasi Printer Tersier',
      description:
        'Konfigurasi printer tersier untuk frame tertentu. Anda dapat memilih frame mana yang akan dicetak menggunakan printer ini. Ini berguna untuk mencetak jenis frame berbeda di printer berbeda.'
    },
    frameTest: {
      title: 'Frame For Print Testing',
      dragAndDrop: 'Drag and drop an image here, or click to select'
    },
    feedback: {
      selectPrinter: 'Pilih printer terlebih dahulu',
      printInitiated: 'Pencetakan Dimulai',
      printFailed: 'Gagal mencetak'
    },
    smartSetup: {
      loading: 'Memuat kemampuan printer...'
    },
    selectFrames: 'Pilih Frame',
    custom: 'Custom',
    noCapabilities: 'Tidak ada kemampuan tersedia - konfigurasi manual',
    preset: 'Preset',
    presetHelp: 'Pilih preset atau pilih Custom untuk mengonfigurasi pengaturan secara manual',
    noCapabilitiesHelp: 'Konfigurasi pengaturan secara manual di bagian Details di bawah',
    details: 'Detail Preset',
    printerDriverDefaults: {
      title: 'Gunakan Default Driver Printer',
      description:
        'Gunakan pengaturan default driver printer untuk margin dan DPI. Kompatibilitas lebih baik dengan driver printer khusus.',
      toggle: 'Alihkan default driver printer'
    },
    csharpPrinting: {
      title: 'C# Direct Printing (Eksperimental)',
      description: 'Gunakan .NET printing untuk keandalan lebih baik dengan printer tertentu',
      toggle: 'Alihkan C# printing'
    },
    printerPurpose: 'Tujuan Printer',
    printerPurposeHelpSecondary:
      'Pilih tujuan untuk printer sekunder: special frames untuk routing berbasis frame, atau parallel untuk distribusi round-robin.',
    printerPurposeHelpTertiary:
      'Pilih tujuan untuk printer tersier: special frames untuk routing berbasis frame, atau parallel untuk distribusi round-robin.',
    specialFramesPrint: 'Cetak frame khusus',
    parallelPrinting: 'Pencetakan Paralel',
    positionShift: 'Perpindahan Posisi Cetak (cm)',
    positionShiftHelp:
      'Sesuaikan perataan cetak. Menyesuaikan margin @page secara independen dari pengaturan margin.',
    positionShiftTop: 'Atas',
    positionShiftBottom: 'Bawah',
    positionShiftLeft: 'Kiri',
    positionShiftRight: 'Kanan',
    positionShiftTopTitle: 'Perpindahan posisi atas',
    positionShiftBottomTitle: 'Perpindahan posisi bawah',
    positionShiftLeftTitle: 'Perpindahan posisi kiri',
    positionShiftRightTitle: 'Perpindahan posisi kanan',
    remotePrinterStatus: {
      title: 'Status Remote Printer',
      active: 'Status: Aktif',
      inactive: 'Status: Tidak aktif atau tidak terhubung.',
      name: 'Nama: ',
      description: 'Remote printer aktif. Buka {{appUrl}}/strip untuk mencetak dari jarak jauh.'
    },
    preview: 'Cetak',
    noPreview: 'Tidak ada pratinjau tersedia',
    upload: 'Unggah Template',
    copies: 'Salinan:',
    copiesTitle: 'Jumlah salinan untuk dicetak',
    printResolutionScale: {
      title: 'Skala Resolusi Cetak',
      description:
        'Sesuaikan resolusi gambar cetak akhir. Nilai yang lebih tinggi menghasilkan kualitas lebih baik tetapi ukuran file lebih besar.'
    },
    printResultMinDisplay: {
      title: 'Waktu minimum di layar pemrosesan sebelum Hasil',
      description:
        'Setelah cetak dan penyimpanan selesai, biarkan layar pemrosesan terbuka setidaknya selama detik ini. Gunakan 0 untuk langsung ke Hasil begitu selesai. Hanya berlaku jika cetak diaktifkan dan tamu benar-benar mencetak.',
      suffix: 'detik (0 = mati, maks {{max}})'
    },
    defaultPrinter: 'Printer Default',
    secondaryPrinter: 'Printer Sekunder',
    tertiaryPrinter: 'Printer Tersier'
  },
  capture: {
    getReady: 'Bersiaplah!',
    sessionStarting: 'Sesi akan dimulai dalam',
    startNow: 'Mulai Sekarang',
    orWaitCountdown: 'atau tunggu hitungan mundur',
    lookAtCamera: 'Lihat ke Kamera',
    stayStill: 'Harap tetap diam saat kami mengambil foto Anda...',
    captureFailed: 'Pengambilan Gagal',
    stayStillCapturing: 'Harap tetap diam saat pengambilan gambar',
    retryingMoment: 'Mencoba ulang sebentar lagi...',
    lastCaptured: 'Terakhir diambil',
    noPhotoYet: 'Belum ada foto',
    recording: 'REC',
    errorStartingSessionRecorder: 'Kesalahan memulai perekam sesi',
    errorProcessingSessionVideo: 'Kesalahan memproses video sesi',
    selfPhotoScanQR: 'Pindai untuk memicu dari ponsel',
    selfPhotoQRAlt: 'Picu dari ponsel',
    selfPhotoEndSession: 'Akhiri sesi',
    selfPhotoPhotoCount: '{{count}} foto',
    selfPhotoAutoLoop: 'Auto',
    selfPhotoSessionTimer: 'Waktu sesi',
    selfPhotoEndSessionBtn: 'Akhiri Sesi',
    selfPhotoAutoLoopOn: 'Auto capture aktif',
    selfPhotoAutoLoopOff: 'Auto capture nonaktif',
    selfPhotoManualOnly: 'Manual',
    selfPhotoTriggerUrl: 'URL Pemicu',
    selfPhotoTriggerNow: 'Picu pengambilan sekarang',
    removePhoto: 'Hapus foto'
  },
  select: {
    title: 'Atur foto Anda',
    instruction: 'Klik foto untuk memilih, geser untuk memindahkan, cubit untuk memperbesar',
    processingPhotos: 'Memproses Foto',
    pleaseWait: 'Mohon tunggu saat kami memproses foto Anda...',
    printPhotos: 'Cetak Foto',
    swipeInstructions: 'Geser untuk memindahkan, cubit untuk memperbesar',
    mirror: 'Mirror',
    selectionLocked: 'Pilihan dikunci',
    processing: 'Memproses...',
    processingVideo: 'Memproses Video...',
    next: 'Selanjutnya ({{count}}/{{max}})',
    capture: 'Ambil Gambar',
    selected: 'Terpilih',
    clickToSelect: 'Klik untuk Memilih',
    continue: 'Lanjutkan',
    errorGif: 'Kesalahan membuat GIF animasi',
    stripExportFailed: 'Gagal membuat pratinjau strip',
    errorVideo: 'Kesalahan memproses video',
    errorProcessing: 'Terjadi kesalahan saat memproses foto Anda. Silakan coba lagi.',
    selectPhotos: 'Pilih {{count}}/{{max}} foto',
    exportFailed: 'Gagal membuat strip. Melanjutkan tanpa bingkai...',
    exportTimeout: 'Pembuatan strip membutuhkan waktu lebih lama dari yang diharapkan. Mohon tunggu...'
  },
  retake: {
    title: 'Tidak Puas dengan Beberapa Foto?',
    headline: 'Klik foto untuk ambil ulang',
    maxHint: 'Maks {{count}}',
    pillSessions: '{{count}} tersisa · Sesi {{current}} dari {{max}}',
    remainingSessions: 'Ambil ulang tersisa: {{count}}',
    selectUpTo: 'Pilih hingga',
    photos: 'foto',
    toRetake: 'untuk diambil ulang',
    selectToRetake: 'Pilih foto yang ingin Anda ambil ulang',
    sessionCount: 'Sesi ambil ulang {{current}} dari {{max}}',
    photoAlt: 'Foto {{number}}',
    photoSelectedAlt: 'Foto {{number}} dipilih untuk ambil ulang',
    overlayRetake: 'Ambil ulang',
    overlaySelected: 'Terpilih',
    selectedToRetake: 'Dipilih untuk Diambil Ulang',
    clickToRetake: 'Klik untuk Ambil Ulang',
    retakeSelected: 'Retake Foto Terpilih ({{count}})',
    retakeCount: 'Ambil ulang ({{count}})',
    retakeAll: 'Retake Semua',
    keepAll: 'Simpan semua',
    explanation: 'Foto yang dipilih akan dihapus dan Anda akan mengambil foto baru. Tindakan ini tidak dapat dibatalkan.',
    confirmContinue: 'Lanjutkan dengan semua foto?',
    confirmContinueHint: 'Anda bisa kembali untuk mengambil ulang foto mana pun, atau lanjutkan ke langkah berikutnya.',
    confirmKeepAllTitle: 'Apakah Anda puas dengan semua foto?',
    confirmKeepAllMessage: 'Anda dapat memilih untuk mengambil ulang beberapa foto atau melanjutkan ke langkah berikutnya.',
    back: 'Kembali',
    continue: 'Lanjutkan',
    confirmKeepAllNo: 'Tidak, saya ingin mengambil ulang foto saya',
    confirmKeepAllYes: 'Ya, saya ingin melanjutkan',
    confirmingKeepAll: 'Melanjutkan...'
  },
  result: {
    videoReady: 'Video Anda Siap!',
    photosReady: 'Foto Anda Siap!',
    scanQrInstruction: 'Pindai kode QR di bawah untuk mengakses {{type}} Anda',
    video: 'video',
    digitalPhotos: 'foto digital',
    photoStripAlt: 'Strip Foto',
    qrCodeAlt: 'Kode QR',
    sessionId: 'ID Sesi:',
    scanToView: 'Pindai untuk melihat galeri',
    videoRecording: 'Rekaman Video',
    photoStrip: 'Strip Foto',
    backToHome: 'Kembali ke Beranda',
    satisfaction: {
      question: 'Bagaimana pengalaman bilik foto Anda?',
      rateExperience: 'Beri nilai pengalaman Anda',
      thankYou: 'Terima kasih atas masukan Anda!',
      starAlt: 'Beri nilai {{rating}} bintang'
    },
    emailGallery: {
      title: 'Kirim tautan galeri ke email',
      description: 'Satu pesan berisi tautan ke galeri ini.',
      emailLabel: 'Email',
      emailPlaceholder: 'nama@email.com',
      sendEmail: 'Kirim',
      sendingEmail: 'Mengirim…',
      invalidEmail: 'Masukkan alamat email yang valid.',
      shareSuccess: 'Terkirim! Periksa email Anda.',
      shareError: 'Tidak terkirim. Coba lagi.',
      shareNetworkError: 'Koneksi bermasalah. Coba lagi.',
      close: 'Tutup'
    },
    postSessionAi: {
      title: 'Peningkatan AI',
      subtitle: 'Poles opsional untuk foto Anda.',
      guestPromptLabel: 'Tambahkan catatan (opsional)',
      guestPromptPlaceholder: 'mis. lebih vintage',
      skip: 'Lewati',
      apply: 'Terapkan AI',
      applying: 'Menerapkan…',
      continue: 'Lanjutkan',
      errorGeneric: 'Terjadi kesalahan. Ketuk Lewati untuk lanjut.',
      insufficientCredits: 'AI sedang tidak tersedia. Ketuk Lewati untuk lanjut.',
      missingStrip: 'Strip belum siap. Ketuk Lewati untuk lanjut.',
      missingRaw: 'Belum ada foto untuk AI. Ketuk Lewati untuk lanjut.',
      missingRawNeedHttps: 'Foto masih diunggah. Ketuk Lewati untuk lanjut.',
      timeout: 'AI butuh waktu lebih lama dari biasanya. Ketuk Lewati untuk lanjut.',
      offline: 'AI butuh koneksi internet. Ketuk Lewati untuk lanjut.',
      stripUploading: 'Menyelesaikan unggahan strip…',
      previewBefore: 'Sebelum',
      previewAfter: 'Sesudah',
      previewCompareHeading: 'Pratinjau sebelum dan sesudah',
      previewLoading: 'Memuat pratinjau…',
      previewMissingUrls: 'Belum ada pratinjau.',
      previewLoadError: 'Tidak dapat memuat pratinjau. Coba lagi.',
      previewImageLoadError: 'Tidak dapat memuat pratinjau. Coba lagi.',
      filmstripAriaLabel: 'Foto sesi Anda',
      filmstripCaption: 'Foto sesi',
      filmstripHelper: 'Ketuk foto untuk menghasilkan mode AI darinya.',
      filmstripThumb: 'Foto sesi',
      filmstripThumbWithAi: 'Foto sesi (AI siap)',
      aiProcessedTooltip: 'Versi AI siap',
      dragToCompareHint: 'Geser untuk membandingkan sebelum dan sesudah.',
      previewEmptyMessage: 'Belum ada pratinjau perbandingan. Anda masih bisa menerapkan AI di bawah.',
      pickOneSpecial: 'Pilih 1 foto spesial untuk dipoles AI.',
      rawOnce: 'AI bisa memoles satu foto per sesi.',
      rawUsedBadge: 'AI dipakai'
    }
  },
  filter: {
    nextFilter: 'Filter berikutnya',
    prevFilter: 'Filter sebelumnya'
  },
  frame: {
    framesListbox: 'Bingkai',
    previewAlt: 'Pratinjau'
  },
  payment: {
    skipGuide: 'Perlu melewati pembayaran? Klik ikon uang di pojok kanan atas dan masukkan PIN.',
    qrisLogoAlt: 'Logo QRIS',
    qrCodeAlt: 'Kode QR',
    automaticCheck: 'Status pembayaran akan diperiksa secara otomatis.',
    expired: 'Kode pembayaran kedaluwarsa',
    restartPrompt: 'Klik untuk memulai ulang transaksi',
    restart: 'Mulai Ulang Transaksi',
    successMessage: 'Pembayaran berhasil!',
    notSuccessful: 'Pembayaran tidak berhasil. Silakan coba lagi.',
    offline: 'Tidak ada koneksi internet',
    offlineMessage: 'Silakan periksa koneksi internet Anda dan coba lagi',
    retry: 'Coba Lagi',
    reload: 'Muat Ulang',
    checkingConnection: 'Memeriksa koneksi...',
    networkError: 'Terjadi kesalahan jaringan',
    snapError: 'Pembayaran gagal. Silakan coba lagi.',
    wrongCode: 'Kode salah. Silakan coba lagi.',
    enterCode: 'Masukkan Kode untuk Melanjutkan',
    offlineMode: 'Mode Offline - Tidak Ada Internet',
    autoRestart: 'Mulai ulang otomatis dalam',
    snapReady: 'Gateway pembayaran siap',
    snapOpening: 'Membuka pembayaran...',
    openSnap: 'Buka Pembayaran',
    payNowTitle: 'BAYAR UNTUK MEMULAI SESI',
    codeEntry: {
      title: 'Masukkan Kode Akses',
      subtitle: 'Masukkan kode 6-digit untuk melewati pembayaran dalam mode offline'
    }
  },
  checkout: {
    title: 'Tinjau Pesanan Anda',
    print: 'Cetak',
    choosePrints: 'Pilih jumlah cetakan',
    minus: 'Kurang',
    plus: 'Tambah',
    gif: 'GIF',
    getAnimated: 'Dapatkan gambar animasi',
    recording: 'Rekaman',
    getRecording: 'Dapatkan rekaman sesi foto',
    video: 'Video',
    recordVideo: 'Rekam video',
    free: 'Gratis',
    subtotal: 'Subtotal:',
    discount: 'Diskon:',
    total: 'Total:',
    remainingTime: 'Waktu Tersisa:',
    havePromo: 'Punya kode voucher?',
    proceedToPayment: 'Lanjutkan ke Pembayaran',
    enterPromo: 'Masukkan kode promo',
    voucherCodeTitle: 'Kode voucher',
    voucherCodePlaceholder: 'Ketik atau tempel kode Anda',
    promoApplied: 'Kode promo berhasil diterapkan!',
    invalidPromo: 'Kode promo tidak valid',
    cancel: 'Batal',
    apply: 'Terapkan',
    promoValidationFailed: 'Gagal memvalidasi kode promo. Silakan coba lagi.',
    digital: 'Foto Digital',
    getDigital: 'Dapatkan salinan digital foto Anda',
    keyboard: 'Papan Ketik',
    scanQR: 'Pindai QR',
    scanningQR: 'Memindai kode QR...',
    qrInstructions:
      'Pegang kode QR di depan kamera Anda. Kode promo akan otomatis terdeteksi dan diterapkan.',
    qrScannerActive: 'Pemindai QR Aktif',
    pointCamera: 'Arahkan kamera Anda ke kode QR yang berisi kode promo',
    failedToCaptureImage: 'Gagal mengambil gambar dari kamera',
    errorScanningQR: 'Kesalahan memindai kode QR',
    addNumberOfPeople: 'Tolong pilih jumlah orang.',
    choosePeople: 'Pilih jumlah orang',
    pax: 'Jumlah Orang'
  },
  bahela: {
    welcome: {
      title: 'Selamat Datang',
      subtitle: 'TUAN DAN NONA!',
      promoButton: 'Kode Promo',
      startButton: 'Mulai Sesi',
      tutorial: 'Panduan',
      noCredits: 'Kredit tidak tersedia. Silakan isi ulang kredit Anda.',
      startError: 'Gagal memulai sesi. Silakan coba lagi.',
      discountApplied: 'Diskon sudah diterapkan, silahkan mulai sesi!',
      next: 'Selanjutnya',
      or: 'atau',
      savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
    },
    promoModal: {
      title: 'Masukkan Kode Promo',
      placeholder: 'Kode Promo',
      submit: 'Masukkan',
      cancel: 'Batal',
      promoExpired: 'Kode promo sudah tidak berlaku',
      invalidPromo: 'Kode promo tidak valid',
      percentageDiscountApplied: 'Diskon {{value}}% diterapkan, silahkan mulai sesi!',
      fixedDiscountApplied: 'Diskon {{value}} diterapkan, silahkan mulai sesi!',
      promoError: 'Gagal memvalidasi kode promo',
      cashVoucherApplied: 'Voucher tunai diterapkan.',
      customFrameVoucherApplied:
        'Voucher frame kustom diterapkan. Frame Anda akan dipilih di kiosk.'
    },
    tutorial: {
      title: 'Panduan Bilik Foto BAHeLA'
    }
  },
  recording: {
    title: 'Rekaman',
    type: 'Tipe Rekaman',
    modes: {
      live: 'Live Mode',
      story: 'Story Mode',
      session: 'Session Mode'
    },
    description: {
      recording: 'Video strip foto live mode',
      strip_live: 'Video strip foto live mode',
      strip_story: 'Video strip foto story mode',
      session_recording: 'Rekaman seluruh sesi foto'
    },
    toggle: 'Aktifkan rekaman',
    errorScanningQR: 'Kesalahan memindai kode QR',
    addNumberOfPeople: 'Tolong pilih jumlah orang.',
    choosePeople: 'Pilih jumlah orang',
    pax: 'Jumlah Orang'
  },
  view: {
    noCreditsModal: {
      title: 'Perlu langganan atau kredit',
      description:
        'Langganan Anda telah berakhir atau kredit habis. Perpanjang di dashboard atau isi kredit untuk terus menggunakan aplikasi tanpa gangguan.',
      purchaseSubscription: 'Aktifkan Langganan',
      topUpCredits: 'Isi Ulang Kredit',
      close: 'Tutup'
    },
    subscriptionValidUntil: 'Langganan Anda berlaku hingga {date}.'
  },
  consent: {
    title: 'Persetujuan Publikasi Foto',
    subtitle: 'Kami ingin menampilkan foto-foto menakjubkan Anda!',
    question: 'Bisakah kami menggunakan foto Anda untuk publikasi?',
    description:
      'Foto-foto indah Anda bisa ditampilkan di website, media sosial, atau materi pemasaran kami untuk menginspirasi orang lain. Kami tidak akan pernah membagikan informasi pribadi Anda.',
    yourPhotos: 'Foto Anda',
    yourVideo: 'Video Anda',
    photoStripAlt: 'Strip Foto',
    benefits: {
      title: 'Apa artinya ini:',
      featured: 'Dapat ditampilkan di media sosial atau website kami',
      inspire:
        'Jadikan gaya dan kreativitas unik Anda inspirasi bagi orang lain untuk mencoba pengalaman bilik foto kami',
      privacy: 'Tidak ada informasi pribadi yang akan dibagikan secara publik'
    },
    accept: 'Ya, dengan senang hati!',
    decline: 'Tidak, terima kasih',
    footer:
      'Anda bisa mengubah preferensi ini kapan saja dengan menghubungi tim dukungan kami di dreamboothstudio.com.',
    newspaper: {
      headline: 'SIAP MENJADI TERKENAL?'
    }
  },
  onboarding: {
    congratulations: {
      title: 'Selamat!',
      subtitle: 'Anda telah berhasil menyelesaikan sesi bilik foto pertama Anda!',
      nextSteps: 'Apa yang ingin Anda lakukan selanjutnya?',
      customize: 'Sesuaikan Proyek Anda',
      analytics: 'Lihat Analitik',
      gallery: 'Jelajahi Galeri',
      payment: 'Kelola Langganan',
      getStarted: 'Mulai'
    }
  },
  newspaper: {
    welcome: {
      headline: 'Newspaper Photobooth: {{content}}',
      takePhoto: 'Mulai Foto',
      subheadline: "{{city}}'s Most Beautiful {{year}}",
      subheadlineDefault: "World's Most Beautiful {{year}}",
      introWithCity: 'Citizens from {{city}} have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and local heritage.',
      introDefault: 'Local citizens have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and shared moments.',
      aboutCity: 'About {{city}}',
      photoboothSection: 'Photobooth That Brings It All',
      dreamboothSection: 'Creativity Unleashed By Innovative Software'
    },
    checkout: {
      steps: {
        title: 'Cara Menggunakan',
        step1: 'Langkah 1: Tinjau pesanan Anda dan pilih paket',
        step2: 'Langkah 2: Selesaikan pembayaran jika diperlukan',
        step3: 'Langkah 3: Pilih gaya filter Anda',
        step4: 'Langkah 4: Pilih bingkai foto Anda',
        step5: 'Langkah 5: Ambil foto Anda',
        step6: 'Langkah 6: Tinjau dan ambil ulang jika diperlukan',
        step7: 'Langkah 7: Dapatkan foto Anda!'
      }
    },
    filter: {
      timeRemaining: 'Waktu Tersisa'
    },
    select: {
      timeRemaining: 'Waktu Tersisa'
    }
  },
  strip: {
    step1Title: 'Langkah 1: Pilih Strip',
    step1Desc: 'Pilih bingkai dan unggah foto jika diperlukan.',
    step2Title: 'Langkah 2: Konfigurasi Unggah',
    step2Desc: 'Pilih apakah akan mengunggah ke galeri dan konfigurasi proyek/sesi.',
    step3Title: 'Langkah 3: Konfigurasi Pencetakan Jarak Jauh',
    step3Desc: 'Pilih printer jarak jauh dan konfigurasi pengaturan cetak.',
    step4Title: 'Langkah 4: Pratinjau & Buat',
    step4Desc: 'Tinjau konfigurasi Anda dan buat strip.',
    modeConfig: 'Buat baru',
    modeUpload: 'Unggah gambar strip yang sudah ada',
    useExistingStrip: 'Gunakan strip yang ada',
    chooseFromGallery: 'Pilih dari galeri',
    selectFrame: 'Silakan pilih bingkai',
    selectFrameTitle: 'Pilih Bingkai Strip Foto',
    uploadStripImage: 'Silakan unggah gambar strip foto',
    uploadPhotos: 'Klik untuk mengunggah foto',
    uploadHint: 'Pilih hingga {{count}} foto',
    remove: 'Klik untuk Menghapus',
    mirror: 'Cermin',
    uploadToGallery: 'Unggah ke galeri',
    selectProjectTitle: 'Pilih Proyek',
    enterSessionId: 'Masukkan ID Sesi',
    validatingSession: 'Memvalidasi sesi...',
    sessionValid: 'ID Sesi valid',
    sessionInvalid: 'ID Sesi tidak valid atau tidak diizinkan',
    printStrip: 'Cetak strip',
    selectRemotePrinterTitle: 'Pilih Printer Jarak Jauh',
    noPrinterSelected: 'Tidak ada printer yang dipilih.',
    preview: 'Pratinjau',
    previewHint: 'Pratinjau strip foto Anda',
    generating: 'Membuat...',
    generate: 'Buat Strip',
    downloading: 'Mengunduh...',
    uploading: 'Mengunggah...',
    printing: 'Mencetak...',
    errorFetchingData: 'Kesalahan mengambil data',
    errorFetchingPrinters: 'Gagal mengambil printer jarak jauh',
    exportFailed: 'Gagal membuat pratinjau strip',
    noUser: 'Pengguna tidak terautentikasi',
    invalidSessionId: 'Tidak diizinkan untuk mengunggah ke galeri ini',
    uploadTimeout: 'Unggah membutuhkan waktu lebih lama dari yang diharapkan. Melanjutkan...',
    uploadSuccess: 'Strip berhasil diunggah',
    uploadFailed: 'Gagal mengunggah strip - ID Sesi Tidak Valid',
    printSuccess: 'Pekerjaan cetak berhasil dikirim!',
    printFailed: 'Cetak gagal: {{error}}',
    base64Error: 'Gagal menyiapkan gambar untuk dicetak.',
    printError: 'Kesalahan mengirim pekerjaan cetak.',
    generationError: 'Kesalahan membuat strip foto',
    uploaded: 'Diunggah:',
    step2: {
      upload: 'Unggah',
      skip: 'Lewati'
    },
    step3: {
      print: 'Cetak',
      done: 'Selesai'
    },
    galleryModal: {
      title: 'Pilih Foto dari Galeri',
      selectUpTo: 'Pilih hingga {{count}} foto',
      loading: 'Memuat foto...',
      loadingMore: 'Memuat foto lebih banyak...',
      noPhotos: 'Tidak ada foto ditemukan',
      errorFetching: 'Gagal mengambil foto',
      maxSelected: 'Anda hanya dapat memilih hingga {{count}} foto',
      noSelection: 'Silakan pilih setidaknya satu foto'
    }
  }
}

// Initialize i18n
i18n.use(initReactI18next).init({
  resources: {
    en: {
      translation: enTranslations
    },
    id: {
      translation: idTranslations
    }
  },
  lng: 'en', // Default language
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false // React already escapes values
  },
  react: {
    useSuspense: false
  }
})

export default i18n
